<h2 mat-dialog-title>Collect Payment</h2>

<mat-dialog-content>
  <div *ngIf="loading" class="loading">
    <mat-spinner diameter="32"></mat-spinner>
  </div>

  <div *ngIf="error" class="error">
    {{ error }}
  </div>

  <ng-container *ngIf="!loading && !error">
    <p><strong>Customer:</strong> {{ customerName }}</p>
    <p><strong>Invoice:</strong> <span class="invoice-number" (click)="toggleInvoiceList()" title="Double-click to view other invoices">{{ invoiceNumber || invoiceId }}</span></p>
    
    <!-- Expandable Invoice List -->
    <div class="invoice-list-section" *ngIf="showInvoiceList">
      <div class="invoice-list-header">
        <h4>Select Different Invoice</h4>
        <button mat-icon-button (click)="toggleInvoiceList()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <div *ngIf="loadingInvoices" class="loading-invoices">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading invoices...</span>
      </div>
      
      <div *ngIf="invoiceListError" class="error">
        {{ invoiceListError }}
      </div>
      
      <div *ngIf="!loadingInvoices && !invoiceListError" class="invoice-list">
        <div class="search-box">
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Search invoices</mat-label>
            <input matInput [(ngModel)]="invoiceSearchTerm" (input)="filterInvoices()" placeholder="Invoice number or ID">
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>
        </div>
        
        <div class="invoice-items">
          <div 
            *ngFor="let invoice of filteredPendingInvoices" 
            class="invoice-item"
            [class.current]="invoice.invoiceId === invoiceId"
            (click)="selectInvoice(invoice)"
          >
            <div class="invoice-info">
              <div class="invoice-number">{{ invoice.invoiceNumber || 'INV-' + invoice.invoiceId }}</div>
              <div class="invoice-details">
                <span class="date">{{ invoice.invoiceDate | date:'dd/MM/yyyy' }}</span>
                <span class="amount">{{ invoice.amountResidual | currency: (invoice.currency || 'INR') }}</span>
                <span class="payment-state" [ngClass]="getPaymentStateClass(invoice.paymentState)">
                  {{ getPaymentStateText(invoice.paymentState) }}
                </span>
              </div>
            </div>
            <mat-icon *ngIf="invoice.invoiceId === invoiceId" class="current-icon">check_circle</mat-icon>
          </div>
        </div>
      </div>
    </div>

    <p>
      <strong>Invoice Outstanding:</strong>
      {{ invoiceResidual | currency: currency }}
      <span *ngIf="invoiceDiscount && invoiceDiscount > 0" class="chip">Disc: {{ invoiceDiscount | currency: currency }}</span>
    </p>

    <div class="balance-box" *ngIf="hasCustomerBalance">
      <div class="row">
        <span class="label">Customer Net Balance:</span>
        <span class="value" [ngClass]="{ neg: customerNetBalance < 0 }">
          {{ customerNetBalance | currency: balanceCurrency }}
        </span>
        <span class="chip" *ngIf="customerNetBalance < 0">Credit</span>
      </div>
      <div class="sub">
        R: {{ customerTotalReceivable | currency: balanceCurrency }}
        • P: {{ customerTotalPayable | currency: balanceCurrency }}
      </div>
    </div>

    <!-- Credit Notes Section -->
    <div *ngFor="let credit of creditNotes" class="credit-note-item">
      <mat-checkbox 
        [checked]="isCreditSelected(credit.id)"
        (change)="onCreditSelectionChange(credit.id, $event.checked)"
        class="credit-checkbox">
      </mat-checkbox>
    
      <div class="credit-info">
        <div class="credit-number">{{ credit.name || ('CN-' + credit.id) }}</div>
    
        <div class="credit-details">
          <span class="date">
            {{ credit.date ? (credit.date | date:'dd/MM/yyyy') : '—' }}
          </span>
          <span class="separator">•</span>
          <span>Amount:</span>
          <span class="amount">{{ credit.openAmount | currency:(credit.currency || currency) }}</span>
        </div>
      </div>
    </div>    

    <!-- Payment method -->
    <mat-form-field appearance="fill" class="full-width">
      <mat-label>Payment Method</mat-label>
      <mat-select [(ngModel)]="method" (selectionChange)="onMethodChange()">
        <mat-option value="cash">Cash</mat-option>
        <mat-option value="cheque">Cheque</mat-option>
        <mat-option value="upi">UPI</mat-option>
        <mat-option value="bank">Bank</mat-option>
        <mat-option value="other">Other</mat-option>
      </mat-select>
      <mat-hint>Choose how the customer paid</mat-hint>
    </mat-form-field>

    <!-- Reference -->
    <mat-form-field appearance="fill" class="full-width" *ngIf="showReference">
      <mat-label>{{ referenceLabel }}</mat-label>
      <input
        matInput
        [(ngModel)]="reference"
        [required]="referenceRequired"
        [placeholder]="referencePlaceholder"
      />
      <mat-hint *ngIf="referenceRequired">Reference is required for {{ method | titlecase }} payments.</mat-hint>
    </mat-form-field>

    <!-- Optional note -->
    <mat-form-field appearance="fill" class="full-width">
      <mat-label>Note</mat-label>
      <input matInput [(ngModel)]="note" placeholder="Optional note (e.g. collected at site)" />
    </mat-form-field>

    <!-- Amount -->
    <mat-form-field appearance="fill" class="full-width amount-field">
      <mat-label>Amount to Collect (max {{ getAdjustedAmount() | currency: currency }})</mat-label>
      <input
        matInput
        type="number"
        [(ngModel)]="amount"
        min="0.01"
        [max]="getAdjustedAmount()"
        step="0.01"
        placeholder="Enter amount"
      />
      <mat-hint *ngIf="selectedCreditIds.length > 0">
        Amount adjusted for selected credits. Original: {{ invoiceResidual | currency: currency }}
      </mat-hint>
    </mat-form-field>

    <!-- Invoice Discount -->
    <mat-form-field appearance="fill" class="full-width discount-field">
      <mat-label>Invoice Discount</mat-label>
      <input
        matInput
        type="number"
        [(ngModel)]="invoiceDiscount"
        min="0"
        step="0.01"
        placeholder="Enter invoice-level discount"
        (ngModelChange)="onInvoiceDiscountChange()"
      />
      <mat-hint>This discount will reduce the receivable for this invoice.</mat-hint>
    </mat-form-field>
  </ng-container>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="onCancel()">Cancel</button>
  <button
    mat-raised-button
    color="primary"
    [disabled]="!canSubmit()"
    (click)="onCollect()"
  >
    Collect
  </button>
</mat-dialog-actions>
